/*
 * CodeEditor.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import MonacoEditor, { EditorProps as MonacoEditorProps, OnChange, useMonaco } from "@monaco-editor/react";

// WARNING: Use this only for typing, NEVER use "monacoNs" as a value or it will totally break webpack.
import * as monacoNs from "monaco-editor/esm/vs/editor/editor.api";

import { BEM } from "@adapptio/lib-components";
import { isFunction, isNull } from "@adapptio/lib-shared";

import { DOC_ERROR_SEVERITY, IDocumentError } from "@adapptio/lib-blueprint";

import { initEditor } from "./initEditor";
import { useDeepCompareMemo } from "use-deep-compare";
import { useEditorRootEl } from "../../../hooks";
import { Key } from "ts-key-enum";

initEditor();

export interface ICodeEditorProps extends MonacoEditorProps {
	theme?: "adapptioDark" | MonacoEditorProps["theme"];
	singleLine?: boolean;
	language?: MonacoEditorProps["language"] | "adapptioExpression" | "adapptioExpressionInterpolation";
	parseErrors?: IDocumentError[];
	onEscKeyPressed?: () => void;
	shouldFocus?: boolean;
	readOnly?: boolean;
	debounceTimeout?: number;
	onOuterReferenceClick?: (editorInstance: monacoNs.editor.IStandaloneCodeEditor) => void | Promise<void>;
}

const baseOptions: ICodeEditorProps["options"] = {
	automaticLayout: true,
	padding: {
		top: 4
	},
	fontSize: 12
};

const singleLineOptions: ICodeEditorProps["options"] = {
	padding: {
		top: 4
	},
	fontSize: 12,
	fontWeight: "normal",
	wordWrap: "off",
	lineHeight: 1.5,
	lineNumbers: "off",
	lineNumbersMinChars: 0,
	overviewRulerLanes: 0,
	overviewRulerBorder: false,
	hideCursorInOverviewRuler: true,
	lineDecorationsWidth: 8,
	glyphMargin: false,
	folding: false,
	scrollBeyondLastColumn: 0,
	scrollbar: {
		horizontal: "hidden",
		vertical: "hidden",
		// avoid can not scroll page when hover monaco
		alwaysConsumeMouseWheel: false
	},
	// disable `Find`
	find: {
		addExtraSpaceOnTop: false,
		autoFindInSelection: "never",
		seedSearchStringFromSelection: "never"
	},
	minimap: { enabled: false },
	// see: https://github.com/microsoft/monaco-editor/issues/1746
	wordBasedSuggestions: false,
	// avoid links underline
	links: false,
	// avoid highlight hover word
	occurrencesHighlight: false,
	cursorStyle: "line-thin",
	// hide current row highlight grey border
	// see: https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.ieditoroptions.html#renderlinehighlight
	renderLineHighlight: "none",
	contextmenu: false,
	// default selection is rounded
	roundedSelection: false,
	hover: {
		// unit: ms
		// default: 300
		delay: 100
	},
	acceptSuggestionOnEnter: "on",
	// auto adjust width and height to parent
	// see: https://github.com/Microsoft/monaco-editor/issues/543#issuecomment-321767059
	automaticLayout: true,
	scrollBeyondLastLine: false
};

enum CTX_KEYS {
	SINGLE_LINE = "singleLine"
}

export const CodeEditor: React.FC<ICodeEditorProps> = ({
	className,
	singleLine,
	parseErrors,
	onMount,
	onEscKeyPressed,
	shouldFocus,
	readOnly,
	onChange,
	debounceTimeout,
	onOuterReferenceClick,
	value,
	...props
}) => {
	const Monaco = useMonaco();
	const editorRootEl = useEditorRootEl();
	const [ editorInstance, setEditorInstance ] = useState<monacoNs.editor.IStandaloneCodeEditor>(null);
	const widgetsDomNode = useRef<HTMLElement>(null);

	/* Change hack begin:
	 *   Monaco has some issue that causes call of an old onChange handler instead of the new one.
	 *   This behaviour causes many troubles (overwriting values in UI / model, etc...)
	 *   So we store the current (latest) onChange handler in the ref and we pass generic onChangeWrapper
	 *   handler to Monaco which calls the correct onChange handler inside.
	 */
	const onChangeRef = useRef<OnChange>();
	onChangeRef.current = onChange;

	const onOuterReferenceClickRef = useRef(onOuterReferenceClick);

	const onChangeWrapper = useCallback(
		(value: string | undefined, ev: monacoNs.editor.IModelContentChangedEvent) => {
			onChangeRef.current && onChangeRef.current(value, ev);
		},
		[]
	);

	useEffect(() => {
		onOuterReferenceClickRef.current = onOuterReferenceClick;
	}, [ onOuterReferenceClick ]);

	useEffect(() => {
		return () => {
			onOuterReferenceClickRef.current = undefined;
			onChangeRef.current = undefined;
		};
	}, []);

	const handleEditorMount = useCallback<MonacoEditorProps["onMount"]>(
		(editor, monaco) => {
			if (singleLine) {
				editor.createContextKey(CTX_KEYS.SINGLE_LINE, singleLine);

				if (!globalThis.monacoSingleLineCommandSetup) {
					// disable `Find` widget
					// see: https://github.com/microsoft/monaco-editor/issues/287#issuecomment-328371787
					// eslint-disable-next-line no-bitwise
					editor.addCommand(monaco.KeyMod.CtrlCmd || monaco.KeyCode.KeyF, () => {
						return;
					});

					// ---
					// @Dimi fixed
					// Temporary initialization solution, the problem is that editor
					// is initialized only once in order not to clog the memory.
					// But most likely the first monaco that has a singleLine parameter is initialized,
					// which applies to all the following monaco editors.
					// This disables Enter in the large editor
					// ---

					// disable press `Enter` in case of producing line breaks
					// editor.addCommand(
					// 	monaco.KeyCode.Enter,
					// 	() => {
					// 		// State: https://github.com/microsoft/vscode/blob/1.56.0/src/vs/editor/contrib/suggest/suggestWidget.ts#L50
					// 		const StateOpen = 3;

					// 		// eslint-disable-next-line @typescript-eslint/no-explicit-any
					// 		const suggestWidgetState = (editor as any)?._contributions[
					// 			"editor.contrib.suggestController"
					// 		]?.widget?._value?._state;

					// 		if (suggestWidgetState === StateOpen) {
					// 		/* eslint-disable max-len */
					// 		/**
					// 			/* eslint-enable max-len */
					// 			editor.trigger("", "acceptSelectedSuggestion", undefined);
					// 		}
					// 	}
					// );
					globalThis.monacoSingleLineCommandSetup = true;
				}
			}

			if (!singleLine && !globalThis.monacoMultiLineCommandSetup) {
				editor.addCommand(
					monaco.KeyCode.Escape,
					function () {
						if (onEscKeyPressed) {
							onEscKeyPressed();
						}
					},
					// eslint-disable-next-line max-len
					"!findWidgetVisible && !inReferenceSearchEditor && !editorHasSelection && !parameterHintsVisible && !inlineSuggestionVisible && !suggestWidgetVisible"
				);
				globalThis.monacoMultiLineCommandSetup = true;
			}

			if (isFunction(onMount)) {
				onMount(editor, monaco);
			}

			setEditorInstance(editor);
		},
		[ singleLine, onMount, onEscKeyPressed ]
	);

	// Add parse errors
	useEffect(() => {
		if (editorInstance) {
			if (!Array.isArray(parseErrors) || parseErrors.length === 0) {
				Monaco.editor.setModelMarkers(editorInstance.getModel(), "parser", []);
				return;
			}

			const markers: monacoNs.editor.IMarkerData[] = [];

			for (let i = 0; i < parseErrors.length; i++) {
				let severity;

				switch (parseErrors[i].severity) {
					case DOC_ERROR_SEVERITY.ERROR:
						severity = Monaco.MarkerSeverity.Error;
						break;
					case DOC_ERROR_SEVERITY.WARNING:
						severity = Monaco.MarkerSeverity.Warning;
						break;
					case DOC_ERROR_SEVERITY.INFO:
						severity = Monaco.MarkerSeverity.Info;
						break;
					case DOC_ERROR_SEVERITY.HINT:
						severity = Monaco.MarkerSeverity.Hint;
						break;
				}

				markers.push({
					message: parseErrors[i].message,
					code: parseErrors[i].name,
					severity: severity,
					startLineNumber: parseErrors[i].range.start.line + 1,
					startColumn: parseErrors[i].range.start.col + 1,
					endLineNumber: parseErrors[i].range.end.line + 1,
					endColumn: parseErrors[i].range.end.col + 1
				});
			}

			Monaco.editor.setModelMarkers(editorInstance.getModel(), "parser", markers);
		}
	}, [ editorInstance, parseErrors ]);

	// Set focus?
	useEffect(() => {
		if (editorInstance && shouldFocus) {
			const lineCount = editorInstance.getModel().getLineCount();
			const lastLineMaxColumn = editorInstance.getModel().getLineMaxColumn(lineCount);

			editorInstance.focus();
			editorInstance.setPosition({
				lineNumber: lineCount,
				column: lastLineMaxColumn
			});
		}
	}, [ editorInstance, shouldFocus ]);

	useEffect(() => {
		if (editorInstance && isFunction(onOuterReferenceClickRef.current)) {
			let cmdClick = false;

			const onKeyDown = editorInstance.onKeyDown((event) => {
				cmdClick =
					event.browserEvent.key === Key.Meta && !event.altKey && !event.shiftKey && !event.ctrlKey;

				if (!cmdClick) {
					cmdClick =
						event.browserEvent.key === Key.Control &&
						!event.altKey &&
						!event.shiftKey &&
						!event.metaKey;
				}
			});

			const onDidBlur = editorInstance.onDidBlurEditorWidget(() => {
				if (cmdClick && isFunction(onOuterReferenceClickRef.current)) {
					onOuterReferenceClickRef.current(editorInstance);
				}
			});

			return () => {
				onKeyDown.dispose();
				onDidBlur.dispose();
			};
		}
	}, [ editorInstance ]);

	useEffect(() => {
		// Setup DOM node for widgets
		if (!widgetsDomNode.current) {
			widgetsDomNode.current = document.createElement("div");
			widgetsDomNode.current.classList.add("monaco-editor");
			widgetsDomNode.current.classList.add("edt-code-editor__monaco-overflow");
			editorRootEl.current?.appendChild(widgetsDomNode.current);
		}

		return () => {
			// Remove DOM node for widgets
			if (widgetsDomNode.current) {
				widgetsDomNode.current.parentNode?.removeChild(widgetsDomNode.current);
				widgetsDomNode.current = undefined;
			}
		};
	}, [ widgetsDomNode ]);

	const baseClass = BEM("edt-code-editor", null, {
		"single-line": singleLine
	});

	const cachedOptions: ICodeEditorProps["options"] = useDeepCompareMemo(
		() => ({
			...(singleLine ? singleLineOptions : baseOptions),
			...props.options,
			readOnly,
			overflowWidgetsDomNode: widgetsDomNode.current,
			fixedOverflowWidgets: true
		}),
		[ singleLine, props.options, readOnly, widgetsDomNode?.current ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<MonacoEditor
			{...props}
			value={isNull(value) ? "" : value}
			onChange={onChangeWrapper}
			className={baseClass.toClassName()}
			options={cachedOptions}
			onMount={handleEditorMount}
		/>
	);
};

CodeEditor.defaultProps = {
	theme: "adapptioDark",
	debounceTimeout: 0,
	loading: ""
};
